import { Head, router, useForm } from "@inertiajs/react"
import {
  Plus,
  Pencil,
  Trash2,
  Play,
  Square,
  Upload,
  Mic,
  ImageIcon,
  Link2,
  Unlink,
  CheckCircle2,
  XCircle,
  BookOpen,
  Mail,
} from "lucide-react"
import { useState, useRef, useEffect, useMemo } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const settingsTabs = [
  { label: "General", href: "/app/admin/settings" },
  { label: "Tutorials", href: "/app/admin/tutorial_videos" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArticleCombobox } from "@/components/admin/ArticleCombobox"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

function VoiceDialog({ open, onOpenChange, voice = null }) {
  const isEditing = !!voice
  const form = useForm({
    name: voice?.name || "",
    voice_id: voice?.voiceId || "",
    gender: voice?.gender || "male",
    descriptor: voice?.descriptor || "",
    sample_audio: null,
    profile_image: null,
  })

  const imageInputRef = useRef(null)
  const audioInputRef = useRef(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [audioFileName, setAudioFileName] = useState(null)

  // Reset form state when dialog opens/closes or voice changes
  useEffect(() => {
    if (open) {
      form.setData({
        name: voice?.name || "",
        voice_id: voice?.voiceId || "",
        gender: voice?.gender || "male",
        descriptor: voice?.descriptor || "",
        sample_audio: null,
        profile_image: null,
      })
      form.clearErrors()
      setImagePreview(null)
      setAudioFileName(null)
    }
  }, [open, voice?.id])

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      form.setData("profile_image", file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleAudioChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      form.setData("sample_audio", file)
      setAudioFileName(file.name)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isEditing) {
      form.patch(`/app/admin/voices/${voice.id}`, {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/admin/voices", {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
      setImagePreview(null)
      setAudioFileName(null)
    }
    onOpenChange(isOpen)
  }

  const displayImageUrl = imagePreview || voice?.imageUrl

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Voice" : "Add Voice"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this voice's details and Wondercraft ID."
                : "Add a new voice for podcast generation."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="voice-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="voice-name"
                placeholder="e.g. Sarah"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
              {form.errors.name && (
                <p className="text-sm text-red-600">{form.errors.name}</p>
              )}
            </div>

            {/* Wondercraft Voice ID */}
            <div className="space-y-2">
              <Label htmlFor="voice-id">
                Wondercraft Voice ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="voice-id"
                placeholder="e.g. wc_abc123"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 font-mono text-sm"
                value={form.data.voice_id}
                onChange={(e) => form.setData("voice_id", e.target.value)}
                required
              />
              {form.errors.voice_id && (
                <p className="text-sm text-red-600">{form.errors.voice_id}</p>
              )}
            </div>

            {/* Gender + Descriptor row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={form.data.gender}
                  onValueChange={(value) => form.setData("gender", value)}
                >
                  <SelectTrigger className="w-full bg-white border-zinc-300 text-zinc-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="voice-descriptor">Descriptor</Label>
                <Input
                  id="voice-descriptor"
                  placeholder="e.g. warm, professional"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={form.data.descriptor}
                  onChange={(e) => form.setData("descriptor", e.target.value)}
                />
              </div>
            </div>

            {/* Profile Image Upload */}
            <div className="space-y-2">
              <Label>Profile Image</Label>
              <div className="flex items-center gap-4">
                {displayImageUrl ? (
                  <img
                    src={displayImageUrl}
                    alt=""
                    className="w-14 h-14 rounded-full object-cover border border-zinc-200"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center border border-zinc-200">
                    <ImageIcon className="h-5 w-5 text-zinc-400" />
                  </div>
                )}
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {displayImageUrl ? "Change Image" : "Upload Image"}
                  </Button>
                  <p className="text-xs text-zinc-500 mt-1">
                    WebP, PNG, or JPEG. Max 5MB.
                  </p>
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/webp,image/png,image/jpeg"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>
              {form.errors.profile_image && (
                <p className="text-sm text-red-600">{form.errors.profile_image}</p>
              )}
            </div>

            {/* Audio Sample Upload */}
            <div className="space-y-2">
              <Label>Audio Sample</Label>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-zinc-100 flex items-center justify-center border border-zinc-200">
                  <Mic className="h-5 w-5 text-zinc-400" />
                </div>
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => audioInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {audioFileName || voice?.sampleUrl
                      ? "Change Audio"
                      : "Upload Audio"}
                  </Button>
                  {audioFileName ? (
                    <p className="text-xs text-zinc-700 mt-1 font-medium">
                      {audioFileName}
                    </p>
                  ) : voice?.sampleUrl ? (
                    <p className="text-xs text-zinc-500 mt-1">
                      Existing sample attached
                    </p>
                  ) : (
                    <p className="text-xs text-zinc-500 mt-1">
                      MP3, WAV, or M4A. Max 10MB.
                    </p>
                  )}
                </div>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/mpeg,audio/wav,audio/x-m4a,audio/mp4"
                  className="hidden"
                  onChange={handleAudioChange}
                />
              </div>
              {form.errors.sample_audio && (
                <p className="text-sm text-red-600">{form.errors.sample_audio}</p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                ? "Save Changes"
                : "Add Voice"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function VoiceRow({ voice, onEdit, onDelete, playingId, onTogglePlay }) {
  const isPlaying = playingId === voice.id

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors">
      {/* Avatar */}
      {voice.imageUrl ? (
        <img
          src={voice.imageUrl}
          alt=""
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-zinc-500">
            {voice.name.charAt(0)}
          </span>
        </div>
      )}

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-zinc-900">{voice.name}</p>
          <Badge
            variant="outline"
            className="border-zinc-300 text-zinc-500 font-mono text-[10px] px-1.5"
          >
            {voice.voiceId}
          </Badge>
          {voice.inUse && (
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]">
              In Use
            </Badge>
          )}
        </div>
        {voice.descriptor && (
          <p className="text-xs text-zinc-500">{voice.descriptor}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {voice.sampleUrl && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onTogglePlay(voice)}
            title={isPlaying ? "Stop" : "Play sample"}
            className={isPlaying ? "text-kf-red" : ""}
          >
            {isPlaying ? (
              <Square className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(voice)}
          title="Edit"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(voice)}
          title="Delete"
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

function GhlConnectionCard({ ghlConnection }) {
  const connected = ghlConnection?.connected
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  const handleConnect = () => {
    setConnecting(true)
    router.post("/app/admin/settings/connect_ghl", {}, {
      onFinish: () => setConnecting(false),
    })
  }

  const handleDisconnect = () => {
    setDisconnecting(true)
    router.delete("/app/admin/settings/disconnect_ghl", {
      preserveScroll: true,
      onFinish: () => setDisconnecting(false),
    })
  }

  const formatDate = (iso) => {
    if (!iso) return null
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    })
  }

  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg font-display">
            GoHighLevel Integration
          </CardTitle>
          <CardDescription>
            Connect your GHL Marketplace app for user sync and SSO.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="border-zinc-300 text-zinc-500">
              <XCircle className="h-3 w-3 mr-1" />
              Not Connected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {connected ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-zinc-50 p-4 text-sm space-y-1">
              {ghlConnection.expiresAt && (
                <p className="text-zinc-600">
                  <span className="font-medium text-zinc-700">Token expires:</span>{" "}
                  {formatDate(ghlConnection.expiresAt)}
                </p>
              )}
              {ghlConnection.lastUpdatedAt && (
                <p className="text-zinc-600">
                  <span className="font-medium text-zinc-700">Last refreshed:</span>{" "}
                  {formatDate(ghlConnection.lastUpdatedAt)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnect}
                disabled={connecting}
              >
                <Link2 className="h-4 w-4 mr-2" />
                {connecting ? "Redirecting..." : "Reconnect"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                <Unlink className="h-4 w-4 mr-2" />
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">
              Connect your GoHighLevel account to enable user sync and single
              sign-on. You'll be redirected to GHL to authorize the app.
            </p>
            <Button
              variant="gradient"
              onClick={handleConnect}
              disabled={connecting}
            >
              <Link2 className="h-4 w-4 mr-2" />
              {connecting ? "Redirecting..." : "Connect GoHighLevel"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const OUR_CALLING_MEMBERS = [
  { key: "mark_clevenger", label: "Mark Clevenger", group: "Leadership" },
  { key: "jim_lange", label: "Jim Lange", group: "Leadership" },
  { key: "ray_hilbert", label: "Ray Hilbert", group: "Leadership" },
  { key: "jen_marr_burkholder", label: "Jen Marr-Burkholder", group: "Leadership" },
  { key: "mun_wai_chung", label: "Mun Wai Chung", group: "Advisory Council" },
  { key: "jana_cardona", label: "Jana Cardona", group: "Advisory Council" },
  { key: "david_moody", label: "David Moody", group: "Advisory Council" },
  { key: "mark_queen", label: "Mark Queen", group: "Advisory Council" },
  { key: "tim_harrison", label: "Tim Harrison", group: "Advisory Council" },
  { key: "james_justice", label: "James Justice", group: "Advisory Council" },
]

function OurCallingArticleLinksCard({ ourCallingArticles = {}, publishedArticles = [] }) {
  const [selections, setSelections] = useState(() => {
    const initial = {}
    OUR_CALLING_MEMBERS.forEach((m) => {
      initial[m.key] = ourCallingArticles[m.key] || ""
    })
    return initial
  })
  const [saving, setSaving] = useState(false)

  const handleSave = () => {
    setSaving(true)
    router.patch("/app/admin/settings", {
      our_calling_articles: selections,
    }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  const hasChanges = OUR_CALLING_MEMBERS.some(
    (m) => selections[m.key] !== (ourCallingArticles[m.key] || "")
  )

  // Group members
  const leadership = OUR_CALLING_MEMBERS.filter((m) => m.group === "Leadership")
  const advisory = OUR_CALLING_MEMBERS.filter((m) => m.group === "Advisory Council")

  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-display">
              Our Calling — Article Links
            </CardTitle>
            <CardDescription>
              Assign a Collective article to each person on the Our Calling page. A "View Story" link will appear under their name.
            </CardDescription>
          </div>
          <BookOpen className="h-5 w-5 text-zinc-400" />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Leadership Team</p>
          <div className="space-y-4">
            {leadership.map((member) => (
              <div key={member.key} className="space-y-1">
                <Label className="text-sm font-semibold text-zinc-700">{member.label}</Label>
                <ArticleCombobox
                  showCategoryFilter
                  articles={publishedArticles}
                  value={selections[member.key]}
                  onSelect={(id) =>
                    setSelections((prev) => ({ ...prev, [member.key]: id }))
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Coach Advisory Council</p>
          <div className="space-y-4">
            {advisory.map((member) => (
              <div key={member.key} className="space-y-1">
                <Label className="text-sm font-semibold text-zinc-700">{member.label}</Label>
                <ArticleCombobox
                  showCategoryFilter
                  articles={publishedArticles}
                  value={selections[member.key]}
                  onSelect={(id) =>
                    setSelections((prev) => ({ ...prev, [member.key]: id }))
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t border-zinc-100">
          <Button
            variant="gradient"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? "Saving..." : "Save Article Links"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function BroadcastSettingsCard({ broadcastSettings = {} }) {
  const [form, setForm] = useState(() => ({
    mailing_address: broadcastSettings.mailing_address || "",
    from_email: broadcastSettings.from_email || "",
    reply_to: broadcastSettings.reply_to || "",
  }))
  const [saving, setSaving] = useState(false)

  const handleSave = () => {
    setSaving(true)
    // Flat params (un-nested) — matches settings#broadcast_settings.
    router.patch("/app/admin/settings/broadcast_settings", form, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  const hasChanges = ["mailing_address", "from_email", "reply_to"].some(
    (k) => form[k] !== (broadcastSettings[k] || "")
  )

  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-display">Broadcast Email</CardTitle>
            <CardDescription>
              Settings for admin bulk emails. A physical mailing address is required by
              law (CAN-SPAM) and appears in every broadcast footer — sends are blocked
              until it is set.
            </CardDescription>
          </div>
          <Mail className="h-5 w-5 text-zinc-400" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="broadcast_mailing_address" className="text-sm font-semibold text-zinc-700">
            Mailing address <span className="text-kf-red">*</span>
          </Label>
          <Textarea
            id="broadcast_mailing_address"
            rows={3}
            placeholder={"Kingdom Factor\n123 Main St\nCity, ST 00000"}
            value={form.mailing_address}
            onChange={(e) => setForm((p) => ({ ...p, mailing_address: e.target.value }))}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="broadcast_from_email" className="text-sm font-semibold text-zinc-700">
              From address
            </Label>
            <Input
              id="broadcast_from_email"
              type="email"
              placeholder="admin@kingdomfactor.us"
              value={form.from_email}
              onChange={(e) => setForm((p) => ({ ...p, from_email: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="broadcast_reply_to" className="text-sm font-semibold text-zinc-700">
              Reply-to address
            </Label>
            <Input
              id="broadcast_reply_to"
              type="email"
              placeholder="Defaults to the sending admin"
              value={form.reply_to}
              onChange={(e) => setForm((p) => ({ ...p, reply_to: e.target.value }))}
            />
          </div>
        </div>
        <div className="pt-3 border-t border-zinc-100">
          <Button variant="gradient" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? "Saving..." : "Save Broadcast Settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default function SettingsIndex({ currentUser, voices = [], pendingCount, ghlConnection = {}, ourCallingArticles = {}, publishedArticles = [], broadcastSettings = {} }) {
  const [createOpen, setCreateOpen] = useState(false)
  const [editVoice, setEditVoice] = useState(null)
  const [deleteVoice, setDeleteVoice] = useState(null)
  const [playingId, setPlayingId] = useState(null)
  const audioRef = useRef(null)

  // Audio setup
  useEffect(() => {
    const audio = new Audio()
    audioRef.current = audio

    const handleEnded = () => setPlayingId(null)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.removeEventListener("ended", handleEnded)
      audio.pause()
      audio.src = ""
    }
  }, [])

  const togglePlay = (voice) => {
    const audio = audioRef.current
    if (!audio) return

    if (playingId === voice.id) {
      audio.pause()
      audio.currentTime = 0
      setPlayingId(null)
    } else {
      audio.src = voice.sampleUrl
      audio.play().catch(() => setPlayingId(null))
      setPlayingId(voice.id)
    }
  }

  const grouped = useMemo(() => {
    const female = voices.filter((v) => v.gender === "female")
    const male = voices.filter((v) => v.gender === "male")
    return [
      { label: "Female", voices: female },
      { label: "Male", voices: male },
    ].filter((g) => g.voices.length > 0)
  }, [voices])

  const handleDelete = () => {
    if (!deleteVoice) return
    router.delete(`/app/admin/voices/${deleteVoice.id}`, {
      preserveScroll: true,
      onSuccess: () => setDeleteVoice(null),
    })
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Settings - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={settingsTabs} />

        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Settings</h1>
          <p className="text-zinc-500">
            Manage application settings and podcast voices
          </p>
        </div>

        {/* GHL Integration Section */}
        <GhlConnectionCard ghlConnection={ghlConnection} />

        {/* Our Calling Article Links */}
        <OurCallingArticleLinksCard
          ourCallingArticles={ourCallingArticles}
          publishedArticles={publishedArticles}
        />

        {/* Broadcast Email Settings */}
        <BroadcastSettingsCard broadcastSettings={broadcastSettings} />

        {/* Podcast Voices Section */}
        <Card className="bg-white border-zinc-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-lg font-display">
                Podcast Voices
              </CardTitle>
              <CardDescription>
                Manage the voices available for podcast generation. Each voice needs a valid Wondercraft Voice ID.
              </CardDescription>
            </div>
            <Button variant="gradient" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Voice
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {grouped.length > 0 ? (
              grouped.map((group) => (
                <div key={group.label}>
                  <div className="px-4 py-2 bg-zinc-50 border-y border-zinc-100">
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                      {group.label} ({group.voices.length})
                    </p>
                  </div>
                  {group.voices.map((voice) => (
                    <VoiceRow
                      key={voice.id}
                      voice={voice}
                      onEdit={setEditVoice}
                      onDelete={setDeleteVoice}
                      playingId={playingId}
                      onTogglePlay={togglePlay}
                    />
                  ))}
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <Mic className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">No voices yet</p>
                <p className="text-sm">
                  Add your first voice to get started with podcast generation.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Voice Dialog */}
      <VoiceDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* Edit Voice Dialog */}
      <VoiceDialog
        open={!!editVoice}
        onOpenChange={(open) => !open && setEditVoice(null)}
        voice={editVoice}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteVoice}
        onOpenChange={(open) => !open && setDeleteVoice(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voice</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteVoice?.inUse ? (
                <>
                  <strong>{deleteVoice?.name}</strong> is currently assigned to
                  one or more articles and cannot be deleted. Please reassign
                  those articles first.
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{deleteVoice?.name}</strong>? This action cannot be
                  undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {!deleteVoice?.inUse && (
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
